import { Core } from "@faire/design-tokens";
import { Column } from "@faire/web/slate/Layout";
import { Spacer, getSpacing } from "@faire/web/slate/spacing";
import { FaireMarketTheme } from "@faire/web/slate/Theme/EventsTheme";
import { eventsThemeColors } from "@faire/web/slate/Theme/eventsThemeColors";
import { EventsThemeShape } from "@faire/web/slate/Theme/EventsThemeShape";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { ThemeProvider } from "@faire/web/slate/Theme/ThemeProvider";
import { Typography } from "@faire/web/slate/Typography";
import { ResponsiveContentV2 } from "@faire/web/ui/ResponsiveContentV2";
import React from "react";
import styled from "styled-components";

import { Context } from "@faire/retailer/components/LayoutElementRenderer/__internal__/Context";
import { AnimatedValueProps } from "@faire/retailer/features/FaireMarkets/ValueProps/AnimatedValueProps";

export const HomeFaireMarketValueProps: React.FC = () => {
  const { layoutElement } = React.useContext(Context);

  const valueProps = () => {
    return layoutElement.layout_element_data.map((valueProps) => {
      const simpleTextTile = valueProps.simple_text_tile;

      return {
        title: simpleTextTile?.main_text,
        description: simpleTextTile?.description_text,
        ctaLink: simpleTextTile?.cta_link,
      };
    });
  };

  return (
    <ThemeProvider theme={FaireMarketTheme}>
      <Container>
        <ResponsiveContentV2
          tabletAndAbove={<StyledMarketBadge width="124px" />}
          mobile={<StyledMarketBadge width="92px" />}
        />

        <Content>
          <Header>{layoutElement.main_text}</Header>
          <Spacer size="5x" />
          <StyledAnimatedValueProps
            valueProps={valueProps()}
            textColor={Core.text.default}
          />
        </Content>
      </Container>
    </ThemeProvider>
  );
};

const Container = styled.div`
  overflow: hidden;
  position: relative;
  max-width: 1280px;
  padding: ${getSpacing(112, "7x", 104)};
  margin: 0 auto;
  background-color: ${eventsThemeColors.faireMarket.lightBackground};

  ${responsiveMediaQueries.tablet} {
    padding: ${getSpacing("5x", "7x")};
  }

  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing("5x", "3x")};
  }
`;

const Content = styled(Column).attrs({
  align: {
    mobileAndAbove: "normal",
    tabletAndAbove: "center",
    desktopAndAbove: "normal",
  },
})``;

const Header = styled(Typography).attrs({
  variant: {
    desktopAndAbove: "displayMSerifRegular",
    tabletAndAbove: "displaySSerifRegular",
    mobileAndAbove: "pageHeaderSerifRegular",
  },
})`
  &&& {
    ${responsiveMediaQueries.desktopAndAbove} {
      max-width: 663px;
    }

    ${responsiveMediaQueries.tablet} {
      z-index: 1;
    }

    ${responsiveMediaQueries.mobile} {
      margin-right: 60px;
    }
  }
`;

const StyledAnimatedValueProps = styled(AnimatedValueProps)`
  ${responsiveMediaQueries.tablet} {
    padding: unset;
  }
`;

const StyledMarketBadge = styled(EventsThemeShape.faireMarket.badge).attrs({
  color: eventsThemeColors.faireMarket.badgeIcon,
})`
  position: absolute;
  top: ${getSpacing("10x")};
  right: ${getSpacing("10x")};

  ${responsiveMediaQueries.tabletAndBelow} {
    top: ${getSpacing("4x")};
    right: -${getSpacing("4x")};
  }
`;
